<template>
  <CustomContainer :showAddButton="false">
    <div class="flex flex-col gap-4 p-2">
      <div class="flex items-center justify-between border_bottom pb-4">
        <div class="flex items-center gap-2">
          <ThemeImage
            :icon="config.icon"
            :alt="`${config.type}_icon`"
            class="size-5"
          />
          <h3 class="text-md font-semibold">
            {{ isEdit ? "Edit" : "" }} {{ config.label }}
          </h3>
        </div>
        <ThemeImage
          v-if="isEdit"
          :icon="imageNames.DELETE_GREY"
          alt="delete_icon"
          class="size-4 cursor-pointer"
          @click="handleDelete"
        />
      </div>

      <div class="form_col gap-2">
        <label for="data_extraction_name" class="secondary_color text-[13px]"
          >{{ config.fields.name.label }} *</label
        >
        <a-input
          id="data_extraction_name"
          v-model:value="formData.name"
          :placeholder="config.fields.name.placeholder"
          class="input_medium"
        />
      </div>

      <div class="form_col gap-2">
        <label for="data_extraction_prompt" class="secondary_color text-[13px]"
          >{{ config.fields.extraction.label }} *</label
        >
        <a-input
          id="data_extraction_prompt"
          v-model:value="formData.extraction"
          :placeholder="config.fields.extraction.placeholder"
          class="input_medium"
        />
      </div>

      <div class="flex justify-start">
        <a-button
          class="primary_medium"
          @click="handleSave"
          :disabled="!isFormValid"
          :loading="isSaving"
        >
          Save
        </a-button>
      </div>
    </div>
  </CustomContainer>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  propertyData: {
    type: Object,
    default: null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  agentId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["save", "delete"]);

const isSaving = ref(false);

const formData = ref({
  name: "",
  extraction: "",
  type: props.config.type,
});

const isFormValid = computed(() => {
  return (
    formData.value.name &&
    formData.value.name.trim() !== "" &&
    formData.value.extraction &&
    formData.value.extraction.trim() !== ""
  );
});

const handleSave = async () => {
  try {
    if (!isFormValid.value) {
      return;
    }

    if (!props.agentId) {
      message.error("Agent ID is missing");
      return;
    }

    isSaving.value = true;

    const payload = {
      voice_agent_id: props.agentId,
      data_name: formData.value.name.trim(),
      extraction_type: formData.value.type,
      extraction_prompt: formData.value.extraction.trim(),
    };

    let response;
    if (props.isEdit && formData.value.id) {
      response = await VoiceAgentService.updateDataExtraction(
        formData.value.id,
        payload
      );
    } else {
      response = await VoiceAgentService.createDataExtraction(payload);
    }

    if (response?.success) {
      const savedData = {
        id: response.data?.id || formData.value.id,
        name: formData.value.name,
        extraction: formData.value.extraction,
        type: formData.value.type,
        created_at:
          response.data?.created_at ||
          formData.value.created_at ||
          new Date().toISOString(),
        updated_at: response.data?.updated_at || new Date().toISOString(),
      };
      emit("save", savedData);
    } else {
      message.error(
        `Failed to ${props.isEdit ? "update" : "create"} data extraction`
      );
    }
  } catch (error) {
    console.error(
      `Error ${props.isEdit ? "updating" : "creating"} data extraction:`,
      error
    );
    message.error(
      `Failed to ${props.isEdit ? "update" : "create"} data extraction`
    );
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async () => {
  try {
    if (!formData.value.id) {
      emit("delete");
      return;
    }
    const response = await VoiceAgentService.deleteDataExtraction(
      formData.value.id
    );
    if (response?.success) {
      emit("delete");
    }
  } catch (error) {
    console.error("Error deleting data extraction:", error);
    message.error("Failed to delete data extraction");
  }
};

const populateFormData = (newData) => {
  try {
    if (newData) {
      formData.value = { ...newData };
    } else {
      formData.value = {
        name: "",
        extraction: "",
        type: props.config.type,
      };
    }
  } catch (error) {
    console.error("Error populating form data:", error);
  }
};

defineExpose({
  formData,
});

watch(() => props.propertyData, populateFormData, { immediate: true });
</script>


